// StewChatPanelLazyMount — lazy boundary that defers the
// StewChatPanel component tree until the panel is opened for the
// first time in this app load.
//
// Before this wrapper: StewChatPanel was mounted on every page paint
// (returns null early when closed, so DOM cost was zero — but the
// component itself ran its useState/useMemo/useCallback initialization,
// imported every card sub-component, and instantiated the
// useStewSession hook). On a Pulse dashboard load that's ~30-50ms
// of work the user never asked for.
//
// After: the wrapper subscribes only to `chrome.isAskStewOpen` and
// flips a local `hasOpenedOnce` flag the first time that's true.
// While the flag is false, NOTHING from the StewChatPanel chunk is
// in scope. Once true, React mounts the real panel and keeps it
// mounted for the rest of the session (so subsequent opens are
// instant — no second cold-start cost).
//
// React.lazy + Suspense gives us code-splitting for free: the entire
// StewChatPanel chunk only downloads on first ⌘K. Useful for the
// Pages bundle since a fresh page load that never opens Stew never
// pays the cost.

import { Suspense, useEffect, useState } from "react";

import { lazyWithReload as lazy } from "../../lib/lazy-with-reload";
import { useChrome } from "../ChromeContext";

const StewChatPanel = lazy(() =>
  import("./StewChatPanel").then((m) => ({ default: m.StewChatPanel })),
);

// Module-scoped flag: any StewChatPanelLazyMount in the tree shares
// the open-once state. There's only ever one instance (mounted by
// PageLayout) so a module-level boolean is enough. We still expose
// it via a hook so React knows to re-render the wrapper when it
// flips from false → true.
let GLOBAL_HAS_OPENED = false;

function useHasOpenedOnce(currentlyOpen: boolean): boolean {
  const [hasOpened, setHasOpened] = useState(GLOBAL_HAS_OPENED);
  useEffect(() => {
    if (currentlyOpen && !GLOBAL_HAS_OPENED) {
      GLOBAL_HAS_OPENED = true;
      setHasOpened(true);
    }
  }, [currentlyOpen]);
  return hasOpened;
}

export function StewChatPanelLazyMount() {
  const { isAskStewOpen } = useChrome();
  const hasOpenedOnce = useHasOpenedOnce(isAskStewOpen);
  if (!hasOpenedOnce) return null;
  return (
    <Suspense fallback={null}>
      <StewChatPanel />
    </Suspense>
  );
}
